import type { FormManager } from "@ostack.tech/kform";
import { useContext } from "react";

import { FormContext } from "../contexts/FormContext.ts";
import { NoFormManagerError } from "../utils/errors.ts";

/**
 * Hook providing access to the form manager within the context of a form. If a
 * form manager is provided as argument, then the provided argument is returned
 * instead.
 *
 * @param formManager Optional argument which, when provided, is simply returned
 *   as-is instead of the form manager within the context of a form.
 * @returns The form manager within the context of a form or, when provided, the
 *   given form manager argument.
 * @throws {NoFormManagerError} When no form manager was found.
 */
export function useFormManager(formManager?: FormManager): FormManager {
  const formContext = useContext(FormContext);
  const relevantFormManager = formManager ?? formContext?.formManager;
  if (relevantFormManager == null) {
    throw new NoFormManagerError();
  }
  return relevantFormManager;
}
